<script setup>
import PageWithButtons from "@/v2/layouts/shared/PageWithButtons/PageWithButtons.vue";
import ButtonZones from "@/v2/components/ButtonZones/ButtonZones.vue";
import Button from "@/v2/primitives/Button/Button.vue";
import Icon from "@/v2/primitives/Icon/Icon.vue";
import Search from "@/v2/components/Search/Search.vue";
import SideMenu from "@/v2/components/SideMenu/SideMenu.vue";
import SponsorBarSubtle from "@/v2/components/SponsorBar/SponsorBarSubtle.vue";
import SponsorBarCarousel from "@/v2/layouts/shared/SponsorBarCarousel/SponsorBarCarousel.vue";

const searchQuery = defineModel("searchQuery", {
  required: true,
  type: String,
});

defineProps({
  searchOptions: {
    type: Array,
    required: true,
  },
  popularSearchOptions: {
    type: Array,
    required: true,
  },
  isSearchLoading: {
    type: Boolean,
    required: true,
  },
  isTouchScreen: {
    type: Boolean,
    required: true,
  },
  sponsorMedia: {
    type: Array,
    default: () => [],
  },
  showSideMenu: {
    type: Boolean,
    default: true,
  },
  sideMenuItems: {
    type: Array,
    required: true,
  },
  sideMenuSelected: {
    type: String,
    default: null,
  },
  sideMenuOpenOnInit: {
    type: Boolean,
    default: false,
  },
  showQrButton: {
    type: Boolean,
    default: false,
  },
  showSearchButton: {
    type: Boolean,
    default: true,
  },
  visitedSearchItems: {
    type: Array,
    default: () => [],
  },
});

defineEmits([
  "onSideMenuSelected",
  "onSearchSelected",
  "onSearchQueryChanged",
  "onAccessibilityButtonClick",
  "onBackButtonClick",
  "onPageUnmounted",
  "onPageIdle",
]);
</script>

<template>
  <PageWithButtons
    class="categories-page-with-buttons"
    :side-menu-open-on-init
    @on-page-unmounted="(payload) => $emit('onPageUnmounted', payload)"
    @on-page-idle="$emit('onPageIdle')"
  >
    <template
      #button-zones="{
        openSlideMenuInline,
        openSlideMenuOverlay,
        openQrDialog,
      }"
    >
      <ButtonZones>
        <template v-if="showSideMenu" #top-left>
          <Button
            variant="primary"
            elevation="raised"
            tooltip="Side Menu"
            tooltip-position="inline-end"
            data-testid="overflow-menu-button"
            data-cmsid="side-menu-button"
            data-dd="Categories Page: Site Navigator Button"
            @click="openSlideMenuInline"
          >
            <template #left-icon>
              <Icon name="bars" />
            </template>
          </Button>
        </template>
        <template #bottom-left>
          <Button
            variant="primary"
            elevation="raised"
            data-testid="back-button"
            tooltip="Back"
            tooltip-position="inline-end"
            data-dd="Categories Page: Back Button"
            @click="$emit('onBackButtonClick')"
          >
            <template #left-icon>
              <Icon name="arrow-left" />
            </template>
          </Button>
        </template>
        <template #bottom-right>
          <div class="categories-page-with-buttons__bottom-right-buttons">
            <Button
              v-if="showQrButton"
              class="categories-page-with-buttons__qr-button"
              variant="primary"
              elevation="raised"
              data-testid="qr-button"
              tooltip="QR Code"
              tooltip-position="inline-start"
              data-cmsid="qr-button"
              aria-label="Open QR code"
              data-dd="Categories Page: QR Code Button"
              @click="openQrDialog"
            >
              <template #left-icon>
                <Icon name="qrcode" />
              </template>
            </Button>
            <Button
              variant="primary"
              elevation="raised"
              tooltip="Accessibility"
              tooltip-position="inline-start"
              data-dd="Categories Page: Accessibility Button"
              @click="$emit('onAccessibilityButtonClick')"
            >
              <template #left-icon>
                <Icon name="universal-access" />
              </template>
            </Button>
          </div>
        </template>
        <template #top-right>
          <Button
            v-if="showSearchButton"
            variant="primary"
            elevation="raised"
            tooltip="Search"
            tooltip-position="inline-start"
            data-dd="Categories Page: Search Button"
            @click="openSlideMenuOverlay"
          >
            <template #left-icon>
              <Icon name="search" />
            </template>
          </Button>
        </template>
      </ButtonZones>
    </template>
    <template #default>
      <div class="categories-page-with-buttons__content">
        <slot />
      </div>
    </template>
    <template #sponsor-bar>
      <div
        v-if="sponsorMedia.length"
        class="categories-page-with-buttons__sponsor-bar"
      >
        <SponsorBarSubtle
          class="categories-page-with-buttons__sponsor-bar--desktop"
          :sponsor-media
        >
          <SponsorBarCarousel :sponsor-media="sponsorMedia" />
        </SponsorBarSubtle>
      </div>
    </template>
    <template #slide-menu-inline-content>
      <SideMenu
        :items="sideMenuItems"
        :selected="sideMenuSelected"
        @selected="
          (e) => {
            $emit('onSideMenuSelected', e);
          }
        "
      />
    </template>
    <template #slide-menu-overlay-content="{ closeSlideMenuOverlay }">
      <Search
        v-model:query="searchQuery"
        :options="searchOptions"
        :popular-options="popularSearchOptions"
        :is-loading="isSearchLoading"
        :show-keyboard="isTouchScreen"
        :touchscreen-mode="isTouchScreen"
        :visited-items="visitedSearchItems"
        @query-changed="$emit('onSearchQueryChanged', $event)"
        @idle="
          () => {
            closeSlideMenuOverlay();
            searchQuery = '';
          }
        "
        @selected="
          (e) => {
            closeSlideMenuOverlay();
            $emit('onSearchSelected', e);
          }
        "
      />
    </template>
  </PageWithButtons>
</template>

<style scoped lang="scss">
@import "@/v2/styles/main";

.categories-page-with-buttons {
  &__content {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__bottom-right-buttons {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    gap: var(--spacing-base);
  }

  &__sponsor-bar {
    position: absolute;
    bottom: 0;
    flex-grow: 0;
    width: 100%;
    pointer-events: none;

    &--desktop {
      display: none;

      @include min-width-sm {
        display: block;
        height: var(--subtle-sponsor-bar-height);
        margin-bottom: var(--spacing-base);
        pointer-events: none;
      }
    }

    &--mobile {
      display: block;

      @include min-width-sm {
        display: none;
      }
    }
  }

  &__qr-button {
    display: none;

    @include min-width-sm-and-height-sm {
      // hide qr code on mobile portrait and landscape
      display: block;
    }
  }
}
</style>
